'use client';
import React from 'react';
import { Dispatch, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import Image from 'next/image';

import { EllaAction, EllaState } from '../Ella/Ella';

import Button from '@/components/common/Button/Button';
import EllaWelcomeMessage from '@/components/common/EllaIntroMessage/EllaIntroMessage';
import EllaPrompt from '@/components/common/Ella/EllaPrompt';
import ProductCardSelect from '@/components/common/ProductCardSelect/ProductCardSelect';
import Text from '@/components/common/Text/Text';
import EllaToast from '@/components/common/EllaToast/EllaToast';
import analytics from '@/util/analytics';
import { useUser } from '@/components/hook/user';

import { Message } from 'ai';
import { ProductCardData } from '@/types/Product';
import EllaFeedback from '@/components/common/EllaFeedback/EllaFeedback';
import { DEFAULT_PRODUCT_CARD_SETTINGS, getCDNUrlForImage } from '@/util/image';

import UserChatIcon from 'public/img/icon/ella-user-chat-icon.svg';
import EllaIcon from 'public/img/icon/ella-chat-icon.svg';

import './EllaMessages.style.scss';
import { PRODUCT_CARD_TYPE } from '../ProductCard/ProductCard';
import { EllaPromptObject } from '@/types/Ella';
import { isValidVivrelleSku } from '@/util/regexp';
import { Cart } from '@/types/Cart';
import { ClosetSlot } from '@/types/Closet';
import { useCart } from '@/components/hook/cart';
import { WishlistCtas } from '../EllaCtas/EllaWishlist';
import { CartCtas } from '../EllaCtas/EllaCart';
import { TryAgainCTA } from '../EllaCtas/EllaTryAgain';
import { usePathname } from 'next/navigation';
import { useEllaAnalytics } from '@/components/hook/analytics';
import { useFlags } from '@/components/hook/flags';
import TextWithAnimatedEllipsis from '../TextWithAnimatedEllipsis/TextWithAnimatedEllipsis';

import { FLAG_KEYS } from '@/const/flags';

export type ProductPromptGridProps = {
  className?: string;
  handlePromptClick: (e: any, prompt: EllaPromptObject) => void;
  handleShowCart: (canBorrowVivrelleItems: boolean) => void;
  handleNewChat: () => void;
  promptsGrid: EllaPromptObject[];
  checkoutDisabled: boolean;
  isUserInLimitedState: boolean;
  canBorrowVivrelleItems: boolean;
};

type EllaMessagesProps = {
  closet: any;
  dispatch: Dispatch<EllaAction>;
  isLoading: boolean;
  handlePromptClick: (e: any, prompt: EllaPromptObject) => void;
  handleNewChat: () => void;
  messages: Message[];
  className?: string;
  accountIsPastDue?: boolean;
  state: EllaState;
  isUserInLimitedState: boolean;
  onLoginSuccess?: () => void;
};

export function ProductPromptGrid(props: ProductPromptGridProps) {
  const {
    handlePromptClick,
    handleShowCart,
    handleNewChat,
    promptsGrid = [],
    checkoutDisabled = false,
    isUserInLimitedState = false,
    canBorrowVivrelleItems = false
  } = props;
  const { metadata } = useEllaAnalytics();

  return (
    <>
      {!isUserInLimitedState && (
        <Button
          onClick={() => {
            analytics.track('Ella AI Checkout with selected items - Clicked', {
              ...metadata
            });
            handleShowCart(canBorrowVivrelleItems);
          }}
          variant='pink-400'
          disabled={checkoutDisabled}
        >
          I love it! Learn more and checkout!
        </Button>
      )}

      {promptsGrid.length > 0 && (
        <div
          className={cn('EllaMessages-prompts-grid', {
            limited: isUserInLimitedState
          })}
        >
          {promptsGrid.map((prompt) => (
            <EllaPrompt
              className='Button EllaMessages-product-btn-alt'
              key={`prompt-${prompt.prompt}`}
              prompt={prompt}
              onClick={(e: any) => {
                handlePromptClick(e, prompt);
              }}
            />
          ))}
        </div>
      )}

      {isUserInLimitedState && (
        <Button
          onClick={handleNewChat}
          variant='pink-400'
        >
          Start a new chat
        </Button>
      )}
    </>
  );
}

// This function should be used as a fallback for messages that used as assistant but don't need to be part of the array of messages
export function SingleMessage(props: { message: string; error?: boolean }) {
  return (
    <div
      className={cn('EllaMessages')}
      style={{ padding: '30px 10px 0 10px' }}
    >
      <div className='EllaMessages-message-container'>
        <div className='EllaMessages-message'>
          <div className={cn('EllaMessages-icon', { user: false })}>
            <Image
              src={EllaIcon}
              height={10}
              width={10}
              alt='Ella chat icon'
              priority
            />
          </div>

          <div>
            <Text
              className={cn('content', { 'text-red': props.error })}
              variant='text-s'
            >
              {props.message}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}

function EllaMessages(props: EllaMessagesProps) {
  const {
    className = '',
    closet,
    dispatch,
    isLoading,
    handlePromptClick,
    handleNewChat,
    messages,
    state,
    isUserInLimitedState
  } = props;

  const [shouldShowAddToWishlistCTA, setShouldShowAddToWishlistCTA] =
    useState<boolean>(false);
  const [shouldShowReplaceItemInCartCTA, setShouldShowReplaceItemInCartCTA] =
    useState<boolean>(false);

  const [newItemToReplaceInCart, setNewItemToReplaceInCart] =
    useState<ProductCardData>();

  // To prevent multiple requests adding items to cart this state need to be on the main component
  // That was part of the ProductCardSelect component
  const [isProcessingProductSelect, setIsProcessingProductSelect] =
    useState<string>();

  const promptsGrid: EllaPromptObject[] = [
    {
      prompt: 'See other accessory options',
      assistant:
        'Same configuration as last user messages but with at least 3 new Vivrelle bag options. Use the newVivrelle shopfilter to get the new Vivrelle bag options'
    },
    {
      prompt: 'See other outfit options',
      assistant:
        'Same configuration as last user messages but with at least 3 non Vivrelle items. Use the newRevolve shopfilter to get the new Revolve/FWRD options'
    },
    ...(isUserInLimitedState
      ? []
      : [
          {
            prompt: 'Skip the Vivrelle item',
            assistant:
              'Include only Revolve outfit/items add only 1 additional Revolve item, and remove Vivrelle bag options. Use the skipVivrelle shopfilter to get the new Revolve/FWRD options'
          },
          {
            prompt: 'Skip the outfit',
            assistant:
              'Include bag from previous configuration (last user messages), must include at least 3 additional Revolve/FWRD options. Use the skipRevolve shopfilter to get the new Vivrelle options'
          }
        ])
  ];
  const { value: cartValue } = useCart();
  const { metadata } = useEllaAnalytics();

  const { value: flagValues } = useFlags();

  const ellaFeedbackIsOn = flagValues[FLAG_KEYS.FLAG_ELLA_FEEDBACK];

  const handleShowCart = () => {
    dispatch({
      type: 'SET_SHOW_CART',
      payload: {
        showCart: true
      }
    });
  };

  /**
   * Handle the submit of the cart
   * Rules to consider before send to cart
   * @param canBorrowVivrelleItems
   * @returns
   */
  const handleSubmitCart = (canBorrowVivrelleItems: boolean) => {
    if (canBorrowVivrelleItems) {
      // Check if customer have something on the cart (out ella) and apply Cart/Closet Eligibility
      const ellaCartHasVivrelleItem = state.cart.some((item) =>
        isValidVivrelleSku(item.sku)
      );

      const hasProductsFromSameTier = state.cart.some(
        (item) =>
          cartValue?.items.some(
            (cartItem: any) =>
              cartItem.product.tier.toLowerCase() === item.tier.toLowerCase() &&
              cartItem.id !== item.id
          )
      );

      // Validate if there are other Vivrelle items in state.cart
      const hasOtherVivrelleItems = state.cart.some(
        (item) =>
          isValidVivrelleSku(item.sku) &&
          !cartValue?.items.some((cartItem) => cartItem.sku === item.sku)
      );

      /**
       * To display the cart the user should have new Vivrelle product in the Ella cart
       * If the user has a different Vivrelle product in the out cart
       * the user can replace the item on the main cart
       */
      if (
        !hasOtherVivrelleItems ||
        !hasProductsFromSameTier ||
        (cartValue as Cart).items.length === 0 ||
        !ellaCartHasVivrelleItem
      ) {
        dispatch({
          type: 'SET_SHOW_CART',
          payload: {
            showCart: true
          }
        });
        return;
      }

      setShouldShowReplaceItemInCartCTA(true);
    } else {
      setShouldShowAddToWishlistCTA(true);
      return;
    }
  };

  // Check if the cart has a Vivrelle item, and set `shouldShowAddToWishlistCTA` to false if not
  useEffect(() => {
    const cartHasVivrelleItem = state.cart.some((item) =>
      isValidVivrelleSku(item.sku)
    );

    if (!cartHasVivrelleItem && shouldShowAddToWishlistCTA) {
      setShouldShowAddToWishlistCTA(false);
    }
  }, [state.cart, shouldShowAddToWishlistCTA]);

  // Each time a new message is added, scroll to top of it
  const latestMessageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      latestMessageRef.current?.scrollIntoView({
        block: 'start',
        behavior: 'smooth'
      });
    }, 100);
  }, [messages]);

  // Determine if we are in ella-in-app in order to do some specific styling
  const currentPath = usePathname();
  const isEllaInApp = currentPath === '/ella-in-app';

  // Store the recommendations for each message ID to avoid weird bugs and changes to prior messages when new messages change
  const recsByMessageId = useRef<
    Record<
      string,
      {
        vivrelleProducts: ProductCardData[];
        revolveProducts: ProductCardData[];
      }
    >
  >({});

  const replaceItemInCart = (product: ProductCardData) => {
    setNewItemToReplaceInCart(product);
    setShouldShowReplaceItemInCartCTA(true);
  };

  return (
    <div className={cn('EllaMessages', className)}>
      <EllaToast onLoginSuccess={props.onLoginSuccess} />

      {messages.length === 0 && <EllaWelcomeMessage />}

      {messages.map((message: Message, messageIndex: number) => {
        const isUser = message.role === 'user';
        if (message.role !== 'assistant' && message.role !== 'user') return;

        const isLatestMessage =
          !isLoading && messages.length === messageIndex + 1;

        // NOTE: the (item.borrowed === undefined) check is a bit of a janky way to determine if there are
        // any items in the cart that were added from within Ella, but it's good enough for now IMO
        const cartIsEmpty = !state.cart.some(
          (item) => item.borrowed === undefined
        );

        const toolInvocations = message.toolInvocations?.filter(
          (invocation) => invocation.toolName
        );
        const recommendationsInvocation = toolInvocations?.find(
          (invocation) => invocation.toolName === 'recommendationsOutput'
        ) as { toolCallId: string; result: any; args?: { message: string } };

        const memoryInvocation = toolInvocations?.find(
          (invocation) => invocation.toolName === 'memoryTool'
        ) as { toolCallId: string; result: any; args?: { message: string } };

        let vivrelleProducts: ProductCardData[] =
          recommendationsInvocation?.result?.vivrelleProducts ||
          memoryInvocation?.result?.vivrelleProducts ||
          [];

        let revolveProducts: ProductCardData[] =
          recommendationsInvocation?.result?.revolveProducts ||
          memoryInvocation?.result?.revolveProducts ||
          [];

        // Handle sessionStorage updates when new recommendations arrive
        if (recommendationsInvocation?.result?.shouldUpdateStorage) {
          try {
            const storeFilter = recommendationsInvocation.result.storeFilter;
            const existingVivrelleProducts = JSON.parse(
              sessionStorage.getItem('vivrelleRecommendations') || '[]'
            );
            const existingRevolveProducts = JSON.parse(
              sessionStorage.getItem('nonVivrelleRecommendations') || '[]'
            );

            switch (storeFilter) {
              case 'both':
                // Update both stores with new items
                sessionStorage.setItem(
                  'vivrelleRecommendations',
                  JSON.stringify(vivrelleProducts)
                );
                sessionStorage.setItem(
                  'nonVivrelleRecommendations',
                  JSON.stringify(revolveProducts)
                );
                break;

              case 'newVivrelle':
                // Update Vivrelle, keep existing Revolve
                sessionStorage.setItem(
                  'vivrelleRecommendations',
                  JSON.stringify(vivrelleProducts)
                );
                // Get stored Revolve items from session storage
                const storedRevolve = JSON.parse(
                  sessionStorage.getItem('nonVivrelleRecommendations') || '[]'
                );
                // Use stored Revolve items
                revolveProducts = storedRevolve;
                break;

              case 'newRevolve':
                // Update Revolve, keep existing Vivrelle
                sessionStorage.setItem(
                  'nonVivrelleRecommendations',
                  JSON.stringify(revolveProducts)
                );
                // Get stored Vivrelle items from session storage
                const storedVivrelle = JSON.parse(
                  sessionStorage.getItem('vivrelleRecommendations') || '[]'
                );
                // Use stored Vivrelle items
                vivrelleProducts = storedVivrelle;
                break;

              case 'skipVivrelle':
                // Add new Revolve item to existing Revolve items
                const updatedRevolveProducts = [
                  ...existingRevolveProducts
                ].slice(0, 4);
                sessionStorage.setItem(
                  'vivrelleRecommendations',
                  JSON.stringify([])
                );
                sessionStorage.setItem(
                  'nonVivrelleRecommendations',
                  JSON.stringify(updatedRevolveProducts)
                );
                revolveProducts = updatedRevolveProducts;
                break;

              case 'skipRevolve':
                // Keep existing Vivrelle, update with new Vivrelle items
                const updatedVivrelleProducts = [
                  ...vivrelleProducts,
                  ...existingVivrelleProducts
                ].slice(0, 4);
                sessionStorage.setItem(
                  'nonVivrelleRecommendations',
                  JSON.stringify([])
                );
                sessionStorage.setItem(
                  'vivrelleRecommendations',
                  JSON.stringify(updatedVivrelleProducts)
                );
                vivrelleProducts = updatedVivrelleProducts;
                break;
            }

            const allRecommendations = [
              ...vivrelleProducts,
              ...revolveProducts
            ];
            sessionStorage.setItem(
              'allRecommendations',
              JSON.stringify(allRecommendations)
            );
          } catch (error) {
            console.error('Error handling sessionStorage:', error);
          }
        }
        const suggestedRepliesInvocation = toolInvocations?.find(
          (invocation) => invocation.toolName === 'suggestedReplies'
        );

        if (recsByMessageId.current[message.id]) {
          revolveProducts = recsByMessageId.current[message.id].revolveProducts;
          vivrelleProducts =
            recsByMessageId.current[message.id].vivrelleProducts;
        } else if (vivrelleProducts.length > 0 || revolveProducts.length > 0) {
          recsByMessageId.current[message.id] = {
            vivrelleProducts,
            revolveProducts
          };
        }

        const revolveProductCards = revolveProducts?.map((product, index) => {
          return (
            <ProductCardSelect
              key={product.id}
              small
              data={product}
              newTab={true}
              variant='ella-styling'
              dispatch={dispatch}
              productType={PRODUCT_CARD_TYPE.REVOLVE}
              price={product.price}
              state={state}
              isUserInLimitedState={isUserInLimitedState}
              isProcessing={isProcessingProductSelect === product.id}
              setIsProcessing={(isProcessing) => {
                setIsProcessingProductSelect(
                  isProcessing ? product.id : undefined
                );
              }}
              isDisabled={Boolean(isProcessingProductSelect)}
            />
          );
        });

        const slotsOpen: string[] | [] = closet?.closet?.slots
          ? closet.closet.slots
              .filter((slot: ClosetSlot) => slot.isOpen)
              .map((slot: ClosetSlot) => slot.maxTier)
          : [];
        // Determine how many Vivrelle products to show based on Revolve products
        const vivrelleDisplayCount = revolveProducts?.length > 0 ? 1 : 4;
        const vivrelleProductCards = vivrelleProducts
          ?.slice(0, vivrelleDisplayCount)
          .map((product, index) => {
            // Because we're streaming, avoid prefixing multiple times
            if (!product.mainImage.startsWith('http')) {
              product.mainImage = getCDNUrlForImage(
                product.mainImage,
                DEFAULT_PRODUCT_CARD_SETTINGS
              );
              if (product.hoverImage) {
                product.hoverImage = getCDNUrlForImage(
                  product.hoverImage,
                  DEFAULT_PRODUCT_CARD_SETTINGS
                );
              }
            }

            return (
              <ProductCardSelect
                key={product.id}
                small
                data={product}
                newTab={true}
                variant='ella-styling'
                dispatch={dispatch}
                productType={
                  !revolveProductCards || revolveProductCards.length === 0
                    ? PRODUCT_CARD_TYPE.VIVRELLE_WITH_BRAND
                    : undefined
                }
                state={state}
                slotsOpen={slotsOpen}
                isUserInLimitedState={isUserInLimitedState}
                isProcessing={isProcessingProductSelect === product.id}
                setIsProcessing={(isProcessing) => {
                  setIsProcessingProductSelect(
                    isProcessing ? product.id : undefined
                  );
                }}
                isDisabled={Boolean(isProcessingProductSelect)}
                replaceItemInCart={replaceItemInCart}
              />
            );
          });

        let content =
          message.content ||
          // If message doesn't contain default content, look for a message in tool invocation, otherwise set to empty string
          recommendationsInvocation?.result?.message ||
          recommendationsInvocation?.args?.message ||
          memoryInvocation?.args?.message ||
          null;

        let additionalContent = null;

        if (suggestedRepliesInvocation?.args?.message) {
          if (!content) {
            content = suggestedRepliesInvocation?.args?.message;
          } else {
            additionalContent = suggestedRepliesInvocation?.args?.message;
          }
        }

        // This don't need to validate cart of web
        const canBorrowVivrelleItems = (closet: any) => {
          // Check if added vivrelle item in the ella cart
          let hasVivrelleItem = false;
          state.cart.map(async (item) => {
            // If the sku is valid, set hasVivrelleItem to true
            if (isValidVivrelleSku(item.sku)) {
              hasVivrelleItem = true;
              return;
            }
          });

          const vivrelleItemInCart: any = state.cart?.filter((item) =>
            isValidVivrelleSku(item.sku)
          );

          // Check if added vivrelle item in the cart
          if (hasVivrelleItem) {
            const itemsSlots = closet?.closet?.slots?.filter(
              (slot: any) => slot.isOpen
            );

            if (!itemsSlots?.length) {
              return false;
            }

            if (
              itemsSlots?.length > 0 &&
              !itemsSlots.some(
                (slot: any) =>
                  slot.maxTier === vivrelleItemInCart[0]?.tier.toLowerCase()
              )
            ) {
              return false;
            }
          }
          return true;
        };

        return (
          <div
            className='EllaMessages-message-container'
            key={`message-${message.id}`}
          >
            {messages.length === messageIndex + 1 && (
              <div
                className={cn(
                  'EllaMessages-latestMessage-scrollToRef',
                  isEllaInApp ? 'ella-in-app' : ''
                )}
                ref={latestMessageRef}
              />
            )}
            <div className='EllaMessages-message'>
              <div className={cn('EllaMessages-icon', { user: isUser })}>
                {isUser ? (
                  <Image
                    src={UserChatIcon}
                    height={16}
                    width={16}
                    alt='User chat icon'
                    priority
                  />
                ) : (
                  <Image
                    src={EllaIcon}
                    height={10}
                    width={10}
                    alt='Ella chat icon'
                    priority
                  />
                )}
              </div>

              <div>
                {content && (
                  <Text
                    className={'content'}
                    variant='text-s'
                  >
                    {content}
                  </Text>
                )}

                {additionalContent && (
                  <Text
                    className={'content'}
                    variant='text-s'
                  >
                    {additionalContent}
                  </Text>
                )}

                {
                  /* Loading message for when Ella has technically started to respond but hasn't streamed any content in yet */
                  isLoading &&
                    messageIndex === messages.length - 1 &&
                    message.role === 'assistant' &&
                    !(content || additionalContent) && (
                      <TextWithAnimatedEllipsis
                        text={'Styling your look'}
                        isItalic
                      />
                    )
                }
              </div>
            </div>

            {/* Try again CTA */}
            {isLatestMessage && recommendationsInvocation?.result?.error && (
              <div className='Ella-recs-and-buttons-container'>
                <div className='Ella-buttons-container'>
                  <TryAgainCTA handlePromptClick={handlePromptClick} />
                </div>
              </div>
            )}

            {/* Outfit suggesting 1 Vivrelle item + 3 Revolve items */}
            {vivrelleProductCards &&
              vivrelleProductCards.length > 0 &&
              revolveProductCards &&
              revolveProductCards.length > 0 && (
                <div className='Ella-recs-and-buttons-container'>
                  <div className='Ella-recommendations-grid'>
                    {vivrelleProductCards[0]}
                    {revolveProductCards.slice(0, 3).map((card, idx) => (
                      <React.Fragment key={`revolve-${idx}-${message.id}`}>
                        {card}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Add feedback component */}
                  {isLatestMessage && ellaFeedbackIsOn && (
                    <EllaFeedback
                      recommendationId={message.id}
                      messages={messages}
                      recommendations={[
                        ...(vivrelleProducts?.map((p) => p.id) || []),
                        ...(revolveProducts?.map((p) => p.id) || [])
                      ]}
                      selectedProducts={state.cart.map((p) => p.id)}
                      availableProducts={
                        recommendationsInvocation?.result?.availableProducts
                      }
                      recommendationPrompt={
                        recommendationsInvocation?.result?.prompt
                      }
                    />
                  )}

                  {/* Show cart button */}
                  {isLatestMessage && (
                    <div className='Ella-buttons-container'>
                      {!shouldShowReplaceItemInCartCTA &&
                      !shouldShowAddToWishlistCTA ? (
                        <ProductPromptGrid
                          handleShowCart={() =>
                            handleSubmitCart(canBorrowVivrelleItems(closet))
                          }
                          handleNewChat={handleNewChat}
                          isUserInLimitedState={isUserInLimitedState}
                          handlePromptClick={handlePromptClick}
                          promptsGrid={promptsGrid}
                          checkoutDisabled={
                            cartIsEmpty ||
                            isUserInLimitedState ||
                            Boolean(isProcessingProductSelect)
                          }
                          canBorrowVivrelleItems={canBorrowVivrelleItems(
                            closet
                          )}
                        />
                      ) : null}
                      {shouldShowAddToWishlistCTA ? (
                        <WishlistCtas
                          handleShowCart={handleShowCart}
                          state={state}
                          dispatch={dispatch}
                        />
                      ) : null}

                      {shouldShowReplaceItemInCartCTA ? (
                        <CartCtas
                          state={state}
                          handleShowCart={handleShowCart}
                          setShouldShowReplaceItemInCartCTA={
                            setShouldShowReplaceItemInCartCTA
                          }
                          dispatch={dispatch}
                          productSelected={newItemToReplaceInCart}
                          setIsProcessing={(isProcessing) => {
                            setIsProcessingProductSelect(
                              isProcessing && newItemToReplaceInCart
                                ? newItemToReplaceInCart.id
                                : undefined
                            );
                          }}
                        />
                      ) : null}
                    </div>
                  )}
                </div>
              )}

            {/* Outfit suggesting 4 Revolve items */}
            {(!vivrelleProductCards || vivrelleProductCards.length === 0) &&
              revolveProductCards &&
              revolveProductCards.length > 0 && (
                <div className='Ella-recs-and-buttons-container'>
                  <div className='Ella-recommendations-grid'>
                    {revolveProductCards.slice(0, 4).map((card, idx) => (
                      <React.Fragment key={`revolve-only-${idx}-${message.id}`}>
                        {card}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Add feedback component */}
                  {isLatestMessage && ellaFeedbackIsOn && (
                    <EllaFeedback
                      recommendationId={message.id}
                      messages={messages}
                      recommendations={[
                        ...(vivrelleProducts?.map((p) => p.id) || []),
                        ...(revolveProducts?.map((p) => p.id) || [])
                      ]}
                      selectedProducts={state.cart.map((p) => p.id)}
                      availableProducts={
                        recommendationsInvocation?.result?.availableProducts
                      }
                      recommendationPrompt={
                        recommendationsInvocation?.result?.prompt
                      }
                    />
                  )}

                  {/* Show checkout button for Revolve items when it's the latest message */}
                  {isLatestMessage && (
                    <div className='Ella-buttons-container'>
                      {/* For Revolve items, we only show the checkout button since wishlist functionality
                       * is currently only implemented for Vivrelle items */}
                      {!isUserInLimitedState && (
                        <Button
                          onClick={() => {
                            analytics.track(
                              'Ella AI Checkout with selected items - Clicked',
                              {
                                ...metadata
                              }
                            );
                            handleShowCart();
                          }}
                          variant='pink-400'
                          disabled={
                            cartIsEmpty || Boolean(isProcessingProductSelect)
                          }
                        >
                          I love it! Learn more and checkout!
                        </Button>
                      )}

                      {handlePromptClick && (
                        <EllaPrompt
                          className='Button EllaMessages-product-btn-alt'
                          prompt={promptsGrid[1]}
                          onClick={(e: any) => {
                            handlePromptClick(e, promptsGrid[1]);
                          }}
                        />
                      )}

                      <Button
                        onClick={handleNewChat}
                        variant='pink-400'
                      >
                        Start a new chat
                      </Button>
                    </div>
                  )}
                </div>
              )}

            {/* Default Vivrelle product carousel  */}
            {vivrelleProductCards &&
              vivrelleProductCards.length > 0 &&
              (!revolveProductCards || revolveProductCards.length === 0) && (
                <div className='Ella-recs-and-buttons-container'>
                  <div className='Ella-recommendations-grid'>
                    {vivrelleProductCards.map((card, idx) => (
                      <React.Fragment
                        key={`vivrelle-only-${idx}-${message.id}`}
                      >
                        {card}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Add feedback component */}
                  {isLatestMessage && ellaFeedbackIsOn && (
                    <EllaFeedback
                      recommendationId={message.id}
                      messages={messages}
                      recommendations={[
                        ...(vivrelleProducts?.map((p) => p.id) || []),
                        ...(revolveProducts?.map((p) => p.id) || [])
                      ]}
                      selectedProducts={state.cart.map((p) => p.id)}
                      availableProducts={
                        recommendationsInvocation?.result?.availableProducts
                      }
                      recommendationPrompt={
                        recommendationsInvocation?.result?.prompt
                      }
                    />
                  )}

                  {/* Show checkout button for Vivrelle items when it's the latest message */}
                  {isLatestMessage && (
                    <div className='Ella-buttons-container'>
                      {/* For Vivrelle items, we only show the checkout button since wishlist functionality
                       * is disabled */}
                      {slotsOpen?.length > 0 && !isUserInLimitedState ? (
                        <Button
                          onClick={handleShowCart}
                          variant='pink-400'
                          disabled={
                            cartIsEmpty || Boolean(isProcessingProductSelect)
                          }
                        >
                          I love it! Learn more and checkout!
                        </Button>
                      ) : null}

                      {handlePromptClick && (
                        <EllaPrompt
                          className='Button EllaMessages-product-btn-alt'
                          prompt={promptsGrid[0]}
                          onClick={(e: any) => {
                            handlePromptClick(e, promptsGrid[0]);
                          }}
                        />
                      )}
                      <Button
                        onClick={handleNewChat}
                        variant='pink-400'
                      >
                        Start a new chat
                      </Button>
                    </div>
                  )}
                </div>
              )}
          </div>
        );
      })}

      {
        /* Loading message for when:
        - user has submitted a message and Ella hasn't started to respond at all yet, OR
        - Ella has sent some content over but the toolInvocation content hasn't finished streaming in */
        isLoading &&
          (messages[messages.length - 1]?.role !== 'assistant' ||
            (!messages[messages.length - 1]?.content &&
              messages[messages.length - 1]?.toolInvocations)) && (
            <div className='EllaMessages-message-container'>
              <div className='EllaMessages-message'>
                <div className={cn('EllaMessages-icon')}>
                  <Image
                    src={EllaIcon}
                    height={10}
                    width={10}
                    alt='Ella chat icon'
                    priority
                  />
                </div>
                <TextWithAnimatedEllipsis
                  text='Styling your look'
                  isItalic
                />
              </div>
            </div>
          )
      }
    </div>
  );
}

export default EllaMessages;
